import { getHeaders } from 'auth/auth';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQuery';

export const underwritingAPI = createApi({
  reducerPath: 'underwriting',
  tagTypes: ['underwritingResults'],
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    // Get underwriting results for an application
    getUnderwritingResults: builder.query({
      query: ({ applicationId, borrowerId }) => {
        const params = new URLSearchParams();
        if (applicationId) params.set('application_id', applicationId);
        if (borrowerId) params.set('borrower_id', borrowerId);
        const queryParams = params.toString() ? `?${params.toString()}` : '';
        return {
          url: `b2b/ai/underwriting${queryParams}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      // This prevents RTK Query from treating 404 as an error, since we want to handle "no results" gracefully
      validateStatus: (response, result) => {
        return response.status === 200 || response.status === 404;
      },
      // Transform the response to handle both successful and 404 cases
      transformResponse: (response, meta) => {
        // If the response is an error message (404), return an empty array
        if (meta?.response?.status === 404) {
          return [];
        }
        if (!response) {
          return [];
        }
        if (Array.isArray(response)) {
          return response;
        }
        const summaryPayload = response.summary || {};
        const analysisResult =
          response.analysis_result ||
          {
            ...summaryPayload,
            remarks: response.remarks || summaryPayload.remarks || [],
          };
        const normalized = {
          ...response,
          analysis_result: analysisResult,
          created_at: response.created_at || response.generated_at,
          file_names: response.file_names || [],
        };
        return [normalized];
      },
      keepUnusedDataFor: 3600, // Keep data for 1 hour
      providesTags: (result, error, { applicationId, userId, borrowerId }) => [
        {
          type: 'underwritingResults',
          id: `${applicationId}-${userId || 'none'}-${borrowerId || 'none'}`
        }
      ],
    }),
    
    // This is now only for manual/direct analysis, not called after /underwrite
    analyzeApplication: builder.mutation({
      query: ({ data, userId, applicationId }) => {
        return {
          url: `ai/underwriting/analyze?user_id=${userId}&application_id=${applicationId}`,
          method: 'POST',
          headers: getHeaders(),
          body: data,
        };
      },
      invalidatesTags: (result, error, { userId, applicationId }) => [
        { type: 'underwritingResults', id: `${applicationId}-${userId}` }
      ],
    }),
  }),
});

export const {
  useGetUnderwritingResultsQuery,
  useLazyGetUnderwritingResultsQuery,
  useAnalyzeApplicationMutation,
} = underwritingAPI;
